'use client';

import { useState, useMemo, useCallback } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useDebounce } from './use-debounce';
import type { PaginatedResponse } from '@/lib/api/types';
import { STALE_TIME } from '@/lib/query/stale-times';
import type { FormOption, InfiniteSelectConfig } from '@/types/base-form';

interface UseInfiniteSelectConfig<
  TData,
  TParams extends Record<string, unknown>
> {
  queryKey: readonly unknown[];
  queryFn: (
    params: TParams & { page: number; per_page: number; search?: string }
  ) => Promise<PaginatedResponse<TData>>;
  mapOption: (item: TData) => FormOption;
  params?: Omit<TParams, 'page' | 'per_page' | 'search'>;
  perPage?: number;
  enabled?: boolean;
  debounceMs?: number;
  /**
   * Override the default reference-data staleness window. Pass
   * `STALE_TIME.live` for a dropdown whose options another actor rewrites while
   * it is open — see `edit-assignment-dialog`, whose available-operator list is
   * consumed by any supervisor assigning work.
   */
  staleTime?: number;
}

function useInfiniteSelect<
  TData,
  TParams extends Record<string, unknown> = Record<string, unknown>
>(config: UseInfiniteSelectConfig<TData, TParams>): InfiniteSelectConfig {
  const {
    queryKey,
    queryFn,
    mapOption,
    params,
    perPage = 20,
    enabled = true,
    debounceMs = 300,
    staleTime = STALE_TIME.reference
  } = config;

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, debounceMs);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery({
      queryKey: [
        ...queryKey,
        'infinite',
        { ...params, search: debouncedSearch }
      ],
      queryFn: ({ pageParam }) =>
        queryFn({
          ...(params as TParams),
          page: pageParam,
          per_page: perPage,
          ...(debouncedSearch ? { search: debouncedSearch } : {})
        } as TParams & { page: number; per_page: number; search?: string }),
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.pagination?.hasNext ? lastPage.pagination.page + 1 : undefined,
      enabled,
      // Dropdown options are reference data. Without this every dialog open
      // refetched them — 81 call sites across 35 files inherit this one line,
      // and reception-listing alone opens five of these queries at once.
      //
      // A newly-created entity still shows up: mutations invalidate the feature
      // key (e.g. `clientKeys.all` / `clientKeys.lists()`), and this hook builds
      // its key as `[...queryKey, 'infinite', ...]` — a prefix match — so
      // invalidation reaches it and overrides staleTime.
      staleTime
    });

  const options = useMemo(() => {
    if (!data?.pages) return [];
    const seen = new Set<string>();
    const result: FormOption[] = [];
    for (const page of data.pages) {
      for (const item of page.data ?? []) {
        const option = mapOption(item);
        if (!seen.has(option.value)) {
          seen.add(option.value);
          result.push(option);
        }
      }
    }
    return result;
  }, [data, mapOption]);

  const onSearchChange = useCallback((value: string) => {
    setSearch(value);
  }, []);

  return {
    options,
    search,
    onSearchChange,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    isLoading
  };
}

export { useInfiniteSelect, type UseInfiniteSelectConfig };
